import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { UiIcon, UiButton } from 'ui-components';
import MobileStepper from '@mui/material/MobileStepper';
import { matchPath, useLocation } from 'react-router-dom';
import { AparatVideo } from 'components';
import { getPages } from './pages';
import { StyledQuickGuide } from './QuickGuide.styles';

export default function QuickGuide({ className }) {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const { pathname } = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    setActiveStepIndex(0);
  }, [pathname]);

  const pages = useMemo(() => getPages(), []);

  const activePage = pages.find((page) => matchPath(page.path, pathname));

  const handleNext = () => {
    setActiveStepIndex((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStepIndex((prevActiveStep) => prevActiveStep - 1);
  };

  const steps = activePage?.steps || [];
  const { text, videoSrc, title } = steps[activeStepIndex] || {};
  const isLastStep = activeStepIndex === steps.length - 1;
  const isFirstStep = activeStepIndex === 0;

  return (
    <StyledQuickGuide>
      <div
        className={classNames(
          'quick-guide',
          steps.length === 1 && 'singular-stepper',
          className
        )}
      >
        <div className='quick-guide-title'>
          <UiIcon icon='ri-lightbulb-line' color='#0C395A' size={18} />
          <span>{t('support:quickGuide')}</span>
        </div>
        {text && <p className='quick-guide-text'>{text}</p>}
        {videoSrc && <AparatVideo src={videoSrc} title={title} />}
        <MobileStepper
          className='ui-stepper'
          variant='dots'
          steps={steps.length}
          position='static'
          activeStep={activeStepIndex}
          sx={{ maxWidth: 400, flexGrow: 1 }}
          nextButton={
            <UiButton
              endIcon={
                <UiIcon
                  icon='ri-arrow-left-s-line'
                  color='#4C6FFF'
                  disabled={isLastStep}
                />
              }
              variant='text'
              color='primary'
              onClick={handleNext}
              disabled={isLastStep}
            >
              {t('layout:next')}
            </UiButton>
          }
          backButton={
            <UiButton
              startIcon={
                <UiIcon
                  icon='ri-arrow-right-s-line'
                  color='#4C6FFF'
                  disabled={isFirstStep}
                />
              }
              variant='text'
              color='primary'
              onClick={handleBack}
              disabled={isFirstStep}
            >
              {t('layout:previous')}
            </UiButton>
          }
        />
      </div>
    </StyledQuickGuide>
  );
}

QuickGuide.propTypes = {
  className: PropTypes.string,
};
